// Scroll-synced audio for the hero film: plays the pour soundtrack from wherever the film is,
// forwards when scrolling down and reversed when scrolling up, at a rate that follows scroll speed.
const RESYNC = 0.3; // seconds of drift before restarting at the film's position
const IDLE_MS = 140; // silence after scrolling stops
const FADE = 0.04;
const MIN_RATE = 0.35;
const MAX_RATE = 3;

const reversed = (ctx, buf) => {
  const out = ctx.createBuffer(buf.numberOfChannels, buf.length, buf.sampleRate);
  for (let c = 0; c < buf.numberOfChannels; c++) {
    out.getChannelData(c).set(buf.getChannelData(c).slice().reverse());
  }
  return out;
};

export const createScrubAudio = (url) => {
  let ctx = null;
  let master = null;
  let buffers = null;
  let loading = null;
  let voice = null; // { src, gain, dir, head }
  let last = null; // { time, at }
  let idle = 0;

  const load = () => {
    if (!loading) {
      loading = fetch(url)
        .then((r) => r.arrayBuffer())
        .then((data) => ctx.decodeAudioData(data))
        .then((buf) => {
          buffers = { fwd: buf, rev: reversed(ctx, buf) };
        });
    }
    return loading;
  };

  const release = () => {
    if (!voice) return;
    const { src, gain } = voice;
    const now = ctx.currentTime;
    gain.gain.cancelScheduledValues(now);
    gain.gain.setValueAtTime(gain.gain.value, now);
    gain.gain.linearRampToValueAtTime(0, now + FADE);
    src.stop(now + FADE + 0.01);
    voice = null;
  };

  const start = (time, dir, rate) => {
    release();
    const buf = dir > 0 ? buffers.fwd : buffers.rev;
    const offset = Math.min(buf.duration - 0.01, Math.max(0, dir > 0 ? time : buf.duration - time));
    const src = ctx.createBufferSource();
    const gain = ctx.createGain();
    src.buffer = buf;
    src.playbackRate.value = rate;
    gain.gain.value = 0;
    gain.gain.linearRampToValueAtTime(1, ctx.currentTime + FADE);
    src.connect(gain).connect(master);
    src.start(0, offset);
    voice = { src, gain, dir, head: time };
  };

  return {
    // Must be called from a click so the browser allows sound.
    async enable() {
      if (!ctx) {
        const AC = window.AudioContext || window.webkitAudioContext;
        ctx = new AC();
        master = ctx.createGain();
        master.gain.value = 0.9;
        master.connect(ctx.destination);
      }
      await ctx.resume();
      await load();
    },
    disable() {
      clearTimeout(idle);
      if (ctx) release();
      last = null;
    },
    // Film time in seconds, fed every animation frame while the film moves.
    update(time) {
      if (!ctx || !buffers || ctx.state !== "running") return;
      const at = performance.now();
      const prev = last;
      last = { time, at };
      if (!prev) return;
      const dt = (at - prev.at) / 1000;
      const dTime = time - prev.time;
      if (dt <= 0 || Math.abs(dTime) < 1e-4) return;
      const dir = dTime > 0 ? 1 : -1;
      const rate = Math.min(MAX_RATE, Math.max(MIN_RATE, Math.abs(dTime) / dt));
      if (voice && voice.dir === dir) {
        voice.head += dir * voice.src.playbackRate.value * dt;
        voice.src.playbackRate.setTargetAtTime(rate, ctx.currentTime, 0.05);
      }
      if (!voice || voice.dir !== dir || Math.abs(voice.head - time) > RESYNC) start(time, dir, rate);
      clearTimeout(idle);
      idle = setTimeout(() => {
        release();
        last = null;
      }, IDLE_MS);
    },
    get ready() {
      return !!buffers;
    },
  };
};
